/**
 * Wording-based matching of a schedule label to a real CRF form (Jim, 2026-10-02).
 *
 * The strict tiers in visitFormMatch.ts (exact, normalised, code, alias) matched
 * nothing on a real 28-form study, because a protocol writes "Haematology (local
 * laboratory)" where the form is "Haematology". This module adds the careful
 * next step and is still safe:
 *
 *   - a label matches a form only when ALL of the label's content words sit in
 *     the form's words (or all of a multi-word form's words sit in the label),
 *   - and EXACTLY ONE form qualifies. Two or more is ambiguous: it stays to be
 *     confirmed and the candidates are recorded,
 *   - a label that reads as dosing or an instruction (a dose, BID/TID/QD, or the
 *     verbs dispense/administer/instruct) is never matched, unless the form's own
 *     name carries that same verb noun (Dispensing, Administration),
 *   - one label maps to several forms only through its separate parenthetical
 *     items, "(UAS7 diary review, DLQI)" giving UAS7 and DLQI.
 *
 * Every match records HOW (contained / acronym / synonym / parenthetical) so a
 * screen can say "matched by wording" and a person can untick it.
 *
 * Pure. No Firestore, no model.
 */
import { normalizeFormName } from "../utils/scheduleFormOrder";
import { canonicalParts } from "./scheduleAliases";
import { isAcronymOf } from "./instrumentNames";

export type WordingHow = "contained" | "acronym" | "synonym" | "parenthetical";

/**
 * Phrase synonyms, applied to the normalised text (words separated by single
 * spaces) BEFORE comparing. Left side is the phrase as written, right side the
 * canonical word(s) both sides are rewritten to. One constant, pinned by a test.
 */
export const SCHEDULE_SYNONYMS: ReadonlyArray<readonly [string, string]> = [
  ["electrocardiogram", "ecg"],
  ["ekg", "ecg"],
  ["labs", "laboratory"],
  ["lab", "laboratory"],
  ["con meds", "concomitant medication"],
  ["conmeds", "concomitant medication"],
  ["concomitant meds", "concomitant medication"],
  ["con med", "concomitant medication"],
  ["inclusion and exclusion", "eligibility"],
  ["inclusion exclusion", "eligibility"],
  ["i e", "eligibility"],
  ["pe", "physical examination"],
  ["end of treatment", "completion"],
  ["end of study", "completion"],
  ["eot", "completion"],
  ["disposition", "completion"],
  ["urine", "urinalysis"],
];

/** Words that carry no identity on their own. */
// "a", "b", single letters and roman numerals are NOT here: "Part A" is not "Part B".
const STOPWORDS = new Set(["and", "or", "of", "the", "with", "for", "to", "on", "in", "at", "by", "per", "local"]);

/**
 * Words a FORM name may carry that the schedule label leaves out. Every other
 * extra word on a form (serious, special, optional, partner, fetal, part, visit,
 * holter...) changes what the form is, so it blocks the match. Kept short:
 *  - complete, full, standard, routine, general: how thorough, not which exam
 *  - prior: "Prior and Concomitant Medications" is the concomitant-medications form
 *  - dipstick: the method of a urinalysis, which a protocol names plain "Urinalysis"
 *  - study: a prefix ("Study Completion", "Study Drug Accountability")
 *  - record, form, assessment: filler a form name appends to a topic
 */
export const FORM_QUALIFIERS: ReadonlySet<string> = new Set([
  "complete", "full", "standard", "routine", "general", "prior", "dipstick", "study", "record", "form", "assessment",
]);

/**
 * Words too generic to identify a form by themselves. A label made only of these
 * ("Study drug", "Visit assessment") never matches by wording, so it cannot
 * pick "Study Drug Accountability" by accident.
 */
export const GENERIC_WORDS: ReadonlySet<string> = new Set([
  "study", "drug", "treatment", "visit", "test", "assessment", "form", "patient", "subject",
  "participant", "data", "screening", "baseline", "review", "report", "check", "record",
]);

/**
 * Lab-panel wording. A schedule names a panel ("Haematology (local laboratory)",
 * "Urinalysis tests") and the form is "Haematology Laboratory Results". These words
 * are dropped from BOTH sides in a second comparison only, and only when real
 * content words remain, so a bare "Laboratory" or "Lab results" never matches.
 * "test" is dropped from the label side only, so "Pregnancy Test" keeps its word.
 */
const LAB_FORM_WORDS: ReadonlySet<string> = new Set(["laboratory", "lab", "result", "panel"]);
const LAB_LABEL_WORDS: ReadonlySet<string> = new Set([...LAB_FORM_WORDS, "test"]);

/** Label-side words an alias label may carry without changing which panel it names. */
const LAB_ABBREV_WORDS: ReadonlySet<string> = new Set(["lab", "panel"]);

function without(a: Set<string>, drop: ReadonlySet<string>): Set<string> {
  return new Set([...a].filter((w) => !drop.has(w)));
}

const DOSE_OR_FREQUENCY =
  /\b\d+(?:\.\d+)?\s*(?:mg|mcg|ug|g|ml|iu|units?|%)\b|\b(?:bid|tid|qd|qid|od|prn|q\d+h)\b/i;
const INSTRUCTION_STEMS = ["dispens", "administ", "instruct"];

function singular(w: string): string {
  return w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w;
}

function applySynonyms(text: string): string {
  let t = ` ${text} `;
  for (const [from, to] of SCHEDULE_SYNONYMS) t = t.split(` ${from} `).join(` ${to} `).split(` ${from} `).join(` ${to} `);
  return t.trim();
}

/** Content words of some text, as a set. */
function words(text: string, useSynonyms: boolean): Set<string> {
  const n = normalizeFormName(text);
  const t = useSynonyms ? applySynonyms(n) : n;
  const out = new Set<string>();
  for (const w of t.split(" ")) {
    if (!w || STOPWORDS.has(w)) continue;
    out.add(INSTRUCTION_STEMS.find((s) => w.startsWith(s)) ?? singular(w));
  }
  return out;
}

/** Text with parenthetical content removed. */
export function withoutParentheses(label: string): string {
  return label.replace(/\([^)]*\)/g, " ").replace(/\s+/g, " ").trim();
}

/** Each item inside parentheses, split on commas, semicolons and "and". */
export function parentheticalItems(label: string): string[] {
  const out: string[] = [];
  for (const m of label.matchAll(/\(([^)]*)\)/g)) {
    for (const part of m[1].split(/\s*(?:,|;|\band\b|&)\s*/i)) {
      const p = part.trim();
      if (p && !out.includes(p)) out.push(p);
    }
  }
  return out;
}

/** Acronyms a form carries in its own parentheses, e.g. "(ECG)", "(UAS7)". */
function formAcronyms(formName: string): string[] {
  const out: string[] = [];
  for (const item of parentheticalItems(formName)) {
    const n = normalizeFormName(item);
    if (n && !n.includes(" ") && n.length >= 3) out.push(singular(applySynonyms(n)));
  }
  return out;
}

/** Does this text carry the acronym in its own parentheses ("Serious Adverse Event (SAE) Report" carries "sae")? */
function carriesOwnAcronym(text: string, acronym: string): boolean {
  // Only a parenthesis that holds the acronym ALONE: "(SAE)". A list of items "(UAS7 diary review, DLQI)" is not a name.
  for (const m of text.matchAll(/\(([^)]*)\)/g)) {
    const n = normalizeFormName(m[1]);
    if (n && !n.includes(" ") && singular(applySynonyms(n)) === acronym) return true;
  }
  return false;
}

/** "(SAE)" as the only parenthesis of "Serious Adverse Event (SAE) Report", with words after it: the label's own acronym, naming the label itself. */
function isOwnAcronymBeforeMoreWords(label: string, item: string): boolean {
  const m = /\(([^)]*)\)\s*(.*)$/.exec(label);
  if (!m) return false;
  return parentheticalItems(label).length === 1 && m[1].trim() === item && normalizeFormName(m[2]) !== "" && isAcronymLike(item);
}
function isAcronymLike(t: string): boolean {
  return (t.match(/[A-Z]/g) ?? []).length >= 2 || (/\d/.test(t) && /[A-Za-z]/.test(t));
}

const NAME_FILLER = new Set(["questionnaire", "scale", "inventory", "index"]);
/** The same name apart from filler words and the parenthetical: "Brief Pain Inventory (BPI) questionnaire" is "Brief Pain Inventory (BPI)". */
function sameCoreName(a: string, b: string): boolean {
  const core = (t: string) => [...words(withoutParentheses(t), false)].filter((w) => !FORM_QUALIFIERS.has(w) && !GENERIC_WORDS.has(w) && !(NAME_FILLER.has(w) && w !== "inventory")).sort().join(" ");
  return core(a) === core(b);
}

function isSubset(a: Set<string>, b: Set<string>): boolean {
  for (const x of a) if (!b.has(x)) return false;
  return true;
}
function onlyGeneric(a: Set<string>): boolean {
  for (const x of a) if (!GENERIC_WORDS.has(x)) return false;
  return true;
}

/** A label that reads as dosing or an instruction. Returns the verb stems it uses, or null when it is dosing outright. */
export function dosingGuard(label: string): { blocked: boolean; stems: string[] } {
  if (DOSE_OR_FREQUENCY.test(label)) return { blocked: true, stems: [] };
  const n = normalizeFormName(label);
  const stems = INSTRUCTION_STEMS.filter((s) => n.includes(s));
  return { blocked: false, stems };
}

interface FormInfo {
  name: string;
  plain: Set<string>;
  syn: Set<string>;
  acronyms: string[];
  norm: string;
  /** The single canonical id of the shared alias table this form stands for, and what is left of its name. */
  alias: { id: string; rest: Set<string> } | null;
}

/**
 * Same-thing-by-the-shared-table: label and form each stand for exactly ONE id of
 * the alias table the coverage check uses, and the same one, with nothing left
 * over but qualifier / generic words. Counts as "synonym".
 */
/**
 * The families of the shared table the matcher honours so far. Only these: the
 * existing strict tests pin "Physical Exam" NOT matching "Physical Examination"
 * (no substring guessing), although the coverage table counts it. Widen one
 * family at a time, with its own regression fixture.
 */
export const ALIAS_MATCH_IDS: ReadonlySet<string> = new Set(["Pregnancy Test", "Haematology"]);

function aliasInfo(text: string, isForm = false): { id: string; rest: Set<string> } | null {
  const p = canonicalParts(text);
  if (p.ids.length !== 1 || !ALIAS_MATCH_IDS.has(p.ids[0])) return null;
  const rest = new Set<string>();
  for (const w of words(p.rest, false)) if (!FORM_QUALIFIERS.has(w) && !GENERIC_WORDS.has(w) && !(isForm ? LAB_FORM_WORDS : LAB_ABBREV_WORDS).has(w)) rest.add(w);
  return { id: p.ids[0], rest };
}

export function prepareForms(names: string[]): FormInfo[] {
  return names.map((name) => ({
    name,
    plain: words(name, false),
    syn: words(name, true),
    acronyms: formAcronyms(name),
    norm: normalizeFormName(name),
    alias: aliasInfo(name, true),
  }));
}

/** Lab-panel comparison: see LAB_FORM_WORDS. Needs a lab word on at least one side to differ from the plain rule. */
function labContains(c: Set<string>, ff: Set<string>): boolean {
  const cl = without(c, LAB_LABEL_WORDS);
  const fl = without(ff, LAB_FORM_WORDS);
  if (fl.size === ff.size) return false; // the form has no lab word: the plain rule already decided
  if (cl.size === 0 || onlyGeneric(cl) || fl.size === 0) return false;
  if (!isSubset(cl, fl)) return false;
  for (const w of fl) if (!cl.has(w) && !FORM_QUALIFIERS.has(w)) return false;
  return true;
}

/** A form that holds all the label's panel words once lab filler is ignored (sibling rule, so the label is ambiguous). */
function labSubset(c: Set<string>, ff: Set<string>): boolean {
  const cl = without(c, LAB_LABEL_WORDS);
  return cl.size > 0 && !onlyGeneric(cl) && isSubset(cl, without(ff, LAB_FORM_WORDS));
}

export interface WordingCandidateResult {
  /** Form names that satisfy the rule for this candidate text, with how. */
  hits: Array<{ form: string; how: Exclude<WordingHow, "parenthetical"> }>;
}

/** Which forms does ONE candidate text qualify for? */
export function formsForCandidate(
  candidate: string,
  forms: FormInfo[],
  stems: string[],
  /** The whole label the candidate came from (so an acronym the label carries in parentheses is known). */
  ownLabel?: string,
): WordingCandidateResult["hits"] {
  const owner = ownLabel ?? candidate;
  const cPlain = words(candidate, false);
  const cSyn = words(candidate, true);
  if (cSyn.size === 0 || onlyGeneric(cSyn)) return [];
  const cAlias = aliasInfo(candidate);
  const aliasHit = new Set<string>();
  const hits: WordingCandidateResult["hits"] = [];
  for (const f of forms) {
    if (stems.some((s) => !f.norm.includes(s))) continue; // an instruction label needs a form of that kind
    if (f.syn.size === 0) continue;
    let how: Exclude<WordingHow, "parenthetical"> | null = null;
    if (f.acronyms.some((a) => cSyn.has(a) && !(carriesOwnAcronym(owner, a) && !sameCoreName(owner, f.name))) || isAcronymOf(candidate, f.name)) how = "acronym"; // two full names sharing an acronym are different forms
    else {
      // ALL of the label's words must be in the form (nothing the form lacks),
      // and every extra word the form carries must be a harmless qualifier.
      const contains = (c: Set<string>, ff: Set<string>) => {
        if (!isSubset(c, ff)) return false;
        for (const w of ff) if (!c.has(w) && !FORM_QUALIFIERS.has(w)) return false;
        return true;
      };
      if (contains(cPlain, f.plain)) how = "contained";
      else if (contains(cSyn, f.syn)) how = "synonym";
      else if (labContains(cPlain, f.plain) || labContains(cSyn, f.syn)) how = "contained";
      else if (cAlias && f.alias && cAlias.id === f.alias.id && cAlias.rest.size === 0 && f.alias.rest.size === 0) {
        how = "synonym";
        aliasHit.add(f.name);
      }
    }
    if (how) hits.push({ form: f.name, how });
  }
  // Sibling rule: a form that matched only by ignoring its qualifier words
  // ("Complete", "Prior") is not unique when another form holds all of the
  // label's words too ("Focused Physical Examination"). Those siblings join the
  // hits, so the label is ambiguous and the candidates are recorded.
  const qualifierOnly = hits.filter((h) => {
    const f = forms.find((x) => x.name === h.form)!;
    return h.how !== "acronym" && !aliasHit.has(h.form) && [...f.syn].some((w) => !cSyn.has(w) && !cPlain.has(w));
  });
  if (qualifierOnly.length) {
    for (const f of forms) {
      if (hits.some((h) => h.form === f.name)) continue;
      if (stems.some((s) => !f.norm.includes(s))) continue;
      if (isSubset(cPlain, f.plain) || isSubset(cSyn, f.syn) || labSubset(cPlain, f.plain) || labSubset(cSyn, f.syn)) hits.push({ form: f.name, how: "contained" });
    }
  }
  return hits;
}

export interface WordingMatch {
  label: string;
  form: string;
  how: WordingHow;
  /** For a parenthetical match, the item that matched. */
  item?: string;
}

export interface WordingOutcome {
  matches: WordingMatch[];
  /** Present when the label was ambiguous: the forms that qualified. */
  ambiguous?: string[];
}

/** The wording rule for one label against the forms. */
export function matchLabelByWording(label: string, forms: FormInfo[]): WordingOutcome {
  const guard = dosingGuard(label);
  if (guard.blocked) return { matches: [] };
  const base = [label, withoutParentheses(label)].filter((t, i, a) => t && a.indexOf(t) === i);
  let ambiguous: string[] | undefined;
  for (const text of base) {
    const hits = formsForCandidate(text, forms, guard.stems, label);
    const names = [...new Set(hits.map((h) => h.form))];
    if (names.length === 1) return { matches: [{ label, form: names[0], how: hits[0].how }] };
    if (names.length > 1 && !ambiguous) ambiguous = names;
  }
  const matches: WordingMatch[] = [];
  for (const item of parentheticalItems(label)) {
    // The label's own acronym ("Serious Adverse Event (SAE) Report" and its "SAE") names the label, not another form.
    if (isOwnAcronymBeforeMoreWords(label, item)) continue;
    const hits = formsForCandidate(item, forms, guard.stems);
    const names = [...new Set(hits.map((h) => h.form))];
    if (names.length === 1 && !matches.some((m) => m.form === names[0])) {
      matches.push({ label, form: names[0], how: "parenthetical", item });
    }
  }
  if (matches.length) return { matches };
  return { matches: [], ...(ambiguous ? { ambiguous } : {}) };
}
